/**
 * Dialog Component
 * Professional confirmation/alert dialog: solid card, circular type icon, clear actions.
 * Part of the global dialog system.
 */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Modal, Pressable, View, Text, TextInput, Platform, Animated } from 'react-native';
import { CdnImage } from '../CdnImage/CdnImage';
import { SoundPressable } from '../SoundPressable/SoundPressable';
import { WEB_HOVER_TRANSITION_STYLE } from '../../constants/animations';
import { AlertCircle, Check, Info, Trophy } from 'lucide-react-native';
import * as Haptics from 'expo-haptics';
import { useTheme } from '../../../theme/useTheme';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useDialogStore } from '../../stores/dialogStore';
import type { QueuedDialog } from '../../stores/dialogStore';
import { useSoundStore } from '../../stores/soundStore';
import { SOUND_IDS } from '../../constants/sounds.constants';
import { getDialogStyles, ICON_SIZE_EXPORT } from './Dialog.styles';
import {
  buildExpectedDeletePhrase,
  normalizeDeletePhraseInput,
} from '../../../account/accountDeletion.constants';
import { detectMobileWebOs } from '../../../onboarding/helpers/detectMobileWebOs.helper';
import { getMobileStoreUrl } from '../../../onboarding/helpers/getMobileStoreUrl.helper';
import { openExternalUrl } from '../../helpers/openExternalUrl.helper';
import { recordWebNativeAppPromoDismissal } from '../../helpers/webNativeAppPromo.helper';
import {
  WEB_NATIVE_APP_PROMO_COPY,
  WEB_NATIVE_APP_PROMO_TEST_IDS,
} from '../../constants/webNativeAppPromo.constants';
import { getAssetUrl, ASSET_PATHS } from '../../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../helpers/imageWebAlt.helper';

const WEB_NATIVE_APP_PROMO_MONKEY_URI = getAssetUrl(ASSET_PATHS.SIGN_IN_MONKEY);

const EXIT_ANIMATION_DURATION = 200;

const ICON_BY_TYPE = {
  danger: AlertCircle,
  default: Info,
  reward: Trophy,
} as const;

export const Dialog: React.FC = () => {
  const currentDialog = useDialogStore((s) => s.currentDialog);
  const closeDialog = useDialogStore((s) => s.closeDialog);
  const handleBackdropPress = useDialogStore((s) => s.handleBackdropPress);
  const theme = useTheme();
  const reduceMotion = useReducedMotion();
  const styles = useMemo(() => getDialogStyles(theme), [theme]);

  // Local state to keep dialog mounted during exit animation
  const [localDialog, setLocalDialog] = useState<QueuedDialog | null>(null);
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Animation values using React Native Animated API
  const scale = useRef(new Animated.Value(0.8)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  const cancelButtonScale = useRef(new Animated.Value(1)).current;
  const destructiveButtonScale = useRef(new Animated.Value(1)).current;
  const confirmButtonScale = useRef(new Animated.Value(1)).current;

  // Update local dialog when currentDialog changes
  useEffect(() => {
    // Clear any pending exit timeout
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }

    if (currentDialog) {
      // New dialog - check if it's different from current localDialog
      if (!localDialog || localDialog.id !== currentDialog.id) {
        // Update local state
        setLocalDialog(currentDialog);

        // A11Y-4: honor reduce-motion — snap to final state, no entry animation.
        if (reduceMotion) {
          scale.setValue(1);
          opacity.setValue(1);
          backdropOpacity.setValue(1);
        } else {
          // Reset and start entry animation immediately
          scale.setValue(0.8);
          opacity.setValue(0);
          backdropOpacity.setValue(0);

          // Animate backdrop fade in
          Animated.timing(backdropOpacity, {
            toValue: 1,
            duration: 200,
            useNativeDriver: Platform.OS !== 'web',
          }).start();

          // Animate dialog with spring (overshoot effect)
          Animated.parallel([
            Animated.spring(scale, {
              toValue: 1,
              tension: 50,
              friction: 7,
              useNativeDriver: Platform.OS !== 'web',
            }),
            Animated.timing(opacity, {
              toValue: 1,
              duration: 300,
              useNativeDriver: Platform.OS !== 'web',
            }),
          ]).start();
        }
      }
    } else if (localDialog) {
      // Dialog was closed - start exit animation
      // Keep localDialog in state until animation completes
      if (reduceMotion) {
        // A11Y-4: snap out, no exit animation.
        scale.setValue(0.9);
        opacity.setValue(0);
        backdropOpacity.setValue(0);
      } else {
        Animated.parallel([
          Animated.timing(scale, {
            toValue: 0.9,
            duration: EXIT_ANIMATION_DURATION,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(opacity, {
            toValue: 0,
            duration: EXIT_ANIMATION_DURATION,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(backdropOpacity, {
            toValue: 0,
            duration: EXIT_ANIMATION_DURATION,
            useNativeDriver: Platform.OS !== 'web',
          }),
        ]).start();
      }

      // Clear local dialog after exit animation completes (immediately when reduced)
      exitTimeoutRef.current = setTimeout(() => {
        setLocalDialog(null);
        exitTimeoutRef.current = null;
      }, reduceMotion ? 0 : EXIT_ANIMATION_DURATION);
    }

    // Cleanup on unmount
    return () => {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
      }
    };
  }, [currentDialog, localDialog, scale, opacity, backdropOpacity, reduceMotion]);

  const isVisible = localDialog !== null;

  // Animated styles
  const dialogAnimatedStyle = {
    transform: [{ scale }],
    opacity,
  };

  const backdropAnimatedStyle = {
    opacity: backdropOpacity,
  };

  const cancelButtonAnimatedStyle = {
    transform: [{ scale: cancelButtonScale }],
  };
  const destructiveButtonAnimatedStyle = {
    transform: [{ scale: destructiveButtonScale }],
  };
  const confirmButtonAnimatedStyle = {
    transform: [{ scale: confirmButtonScale }],
  };

  const [deleteAccountInput, setDeleteAccountInput] = useState('');
  const [nativeAppPromoDontShowAgain, setNativeAppPromoDontShowAgain] = useState(false);

  useEffect(() => {
    if (localDialog?.confirmationMode === 'deleteAccount') {
      setDeleteAccountInput('');
    }
    if (localDialog?.confirmationMode === 'nativeAppPromo') {
      setNativeAppPromoDontShowAgain(false);
    }
  }, [localDialog?.id, localDialog?.confirmationMode]);

  if (!localDialog) {
    return null;
  }

  const finalizeNativeAppPromoDismissal = () => {
    recordWebNativeAppPromoDismissal(nativeAppPromoDontShowAgain);
  };

  const handleConfirm = () => {
    if (localDialog.confirmationMode === 'deleteAccount') {
      const u = localDialog.deleteAccountUsername ?? '';
      if (!u.trim()) return;
      if (
        normalizeDeletePhraseInput(deleteAccountInput) !== buildExpectedDeletePhrase(u)
      ) {
        return;
      }
    }
    if (localDialog.confirmationMode === 'nativeAppPromo') {
      openExternalUrl(getMobileStoreUrl(detectMobileWebOs()));
      finalizeNativeAppPromoDismissal();
    }
    if (Platform.OS !== 'web') {
      try {
        if (localDialog.type === 'danger') {
          Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy).catch(() => {});
        } else {
          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
        }
      } catch {
        // Ignore haptic errors
      }
    }
    if (localDialog.type === 'danger') {
      useSoundStore.getState().playSound(SOUND_IDS.UI_DANGER_COMMIT);
    } else {
      useSoundStore.getState().playUiSuccessBrief();
    }
    closeDialog(true);
  };

  const handleCancel = () => {
    if (localDialog.confirmationMode === 'nativeAppPromo') {
      finalizeNativeAppPromoDismissal();
    }
    if (Platform.OS !== 'web') {
      try {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
      } catch {
        // Ignore haptic errors
      }
    }
    closeDialog(false);
  };

  const handleNativeAppPromoBackdropPress = () => {
    if (localDialog.dismissible === false) return;
    finalizeNativeAppPromoDismissal();
    closeDialog(false);
  };

  const handleDestructive = () => {
    if (Platform.OS !== 'web') {
      try {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {});
      } catch {
        // Ignore haptic errors
      }
    }
    useSoundStore.getState().playSound(SOUND_IDS.UI_DANGER_COMMIT);
    closeDialog('destructive');
  };

  const createButtonPressHandlers = (scaleRef: Animated.Value) => ({
    onPressIn: () => {
      Animated.timing(scaleRef, {
        toValue: 0.95,
        duration: 150,
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    },
    onPressOut: () => {
      Animated.timing(scaleRef, {
        toValue: 1,
        duration: 150,
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    },
  });

  const cancelButtonPressHandlers = createButtonPressHandlers(cancelButtonScale);
  const destructiveButtonPressHandlers = createButtonPressHandlers(destructiveButtonScale);
  const confirmButtonPressHandlers = createButtonPressHandlers(confirmButtonScale);

  const isAlert = localDialog.isAlert;
  const dialogType = localDialog.type || 'default';
  const iconCircleByType =
    dialogType === 'danger'
      ? styles.iconCircleDanger
      : dialogType === 'reward'
        ? styles.iconCircleReward
        : styles.iconCircleDefault;
  const primaryButtonByType =
    dialogType === 'danger'
      ? styles.primaryButtonDanger
      : dialogType === 'reward'
        ? styles.primaryButtonReward
        : styles.primaryButtonDefault;
  const IconComponent = ICON_BY_TYPE[dialogType];

  const isDeleteAccount = localDialog.confirmationMode === 'deleteAccount';
  const isNativeAppPromo = localDialog.confirmationMode === 'nativeAppPromo';
  const hasDestructive =
    Boolean(localDialog.destructiveText) && !isAlert && !isDeleteAccount && !isNativeAppPromo;
  const showCancel =
    !isAlert && localDialog.showCancelButton !== false;
  const deleteUsername = localDialog.deleteAccountUsername ?? '';
  const canConfirmDeleteAccount =
    !isDeleteAccount ||
    (deleteUsername.trim().length > 0 &&
      normalizeDeletePhraseInput(deleteAccountInput) ===
        buildExpectedDeletePhrase(deleteUsername));

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="none"
      onRequestClose={isNativeAppPromo ? handleNativeAppPromoBackdropPress : handleBackdropPress}
      statusBarTranslucent
      presentationStyle="overFullScreen"
      testID="global-dialog"
    >
      <Pressable
        style={[
          styles.backdrop,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        ]}
        onPress={isNativeAppPromo ? handleNativeAppPromoBackdropPress : handleBackdropPress}
        testID="dialog-backdrop"
      >
        <Animated.View
          style={[styles.backdropOverlay, backdropAnimatedStyle]}
          pointerEvents="auto"
        >
          <Animated.View
            style={[
              styles.dialogContainer,
              isNativeAppPromo && styles.dialogContainerNativeAppPromo,
              dialogAnimatedStyle,
            ]}
            pointerEvents="box-none"
          >
            {isNativeAppPromo ? (
              <View style={styles.nativeAppPromoMascotWrap}>
                <CdnImage
                  uri={WEB_NATIVE_APP_PROMO_MONKEY_URI}
                  style={styles.nativeAppPromoMascotImage}
                  contentFit="contain"
                  alt={WEB_IMAGE_ALT_DECORATIVE}
                  testID={WEB_NATIVE_APP_PROMO_TEST_IDS.MASCOT}
                />
              </View>
            ) : null}
            <View
              style={[
                styles.dialogContent,
                isNativeAppPromo && styles.dialogContentNativeAppPromo,
              ]}
              onStartShouldSetResponder={() => true}
              pointerEvents="auto"
              accessibilityViewIsModal
              accessibilityRole={isAlert ? 'alert' : undefined}
              accessibilityLabel={localDialog.title}
            >
              {!isNativeAppPromo ? (
                <View style={[styles.iconCircle, iconCircleByType]}>
                  {localDialog.icon ? (
                    <Text style={styles.iconText}>{localDialog.icon}</Text>
                  ) : (
                    <View style={styles.iconInner}>
                      <IconComponent
                        size={ICON_SIZE_EXPORT}
                        color={
                          dialogType === 'danger'
                            ? theme.error
                            : dialogType === 'reward'
                              ? theme.accent
                              : theme.info
                        }
                      />
                    </View>
                  )}
                </View>
              ) : null}

              {isNativeAppPromo ? (
                <View
                  style={styles.nativeAppPromoTitleSpacer}
                  testID={WEB_NATIVE_APP_PROMO_TEST_IDS.TITLE_TOP_SPACER}
                />
              ) : null}

              <Text style={styles.title}>{localDialog.title}</Text>

              {localDialog.message ? (
                <Text style={styles.message}>{localDialog.message}</Text>
              ) : null}

              {isDeleteAccount && deleteUsername.trim().length > 0 ? (
                <>
                  <Text style={styles.deleteAccountHint} accessibilityRole="text">
                    Type: {buildExpectedDeletePhrase(deleteUsername)}
                  </Text>
                  <TextInput
                    value={deleteAccountInput}
                    onChangeText={setDeleteAccountInput}
                    style={styles.deleteAccountInput}
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="off"
                    textContentType="none"
                    placeholder={buildExpectedDeletePhrase(deleteUsername)}
                    placeholderTextColor={theme.gray[500]}
                    accessibilityLabel="Type phrase to confirm account deletion"
                    testID="dialog-delete-account-input"
                  />
                </>
              ) : null}

              {isNativeAppPromo ? (
                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.nativeAppPromoCheckboxRow,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.buttonPressed,
                    hovered && Platform.OS === 'web' && styles.buttonHover,
                  ]}
                  onPress={() => setNativeAppPromoDontShowAgain((prev) => !prev)}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: nativeAppPromoDontShowAgain }}
                  accessibilityLabel={WEB_NATIVE_APP_PROMO_COPY.DONT_SHOW_AGAIN}
                  testID={WEB_NATIVE_APP_PROMO_TEST_IDS.CHECKBOX}
                >
                  <View
                    style={[
                      styles.nativeAppPromoCheckboxBox,
                      nativeAppPromoDontShowAgain && styles.nativeAppPromoCheckboxBoxChecked,
                    ]}
                  >
                    {nativeAppPromoDontShowAgain ? (
                      <Check size={14} color={theme.info} strokeWidth={3} />
                    ) : null}
                  </View>
                  <Text style={styles.nativeAppPromoCheckboxLabel}>
                    {WEB_NATIVE_APP_PROMO_COPY.DONT_SHOW_AGAIN}
                  </Text>
                </Pressable>
              ) : null}

              <View style={styles.buttonContainer}>
                {showCancel ? (
                  <Animated.View style={cancelButtonAnimatedStyle}>
                    <SoundPressable
                      playTapSound={false}
                      style={({ pressed, hovered }) => [
                        styles.button,
                        styles.secondaryButton,
                        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                        pressed && styles.buttonPressed,
                        hovered && Platform.OS === 'web' && styles.buttonHover,
                      ]}
                      onPress={handleCancel}
                      onPressIn={cancelButtonPressHandlers.onPressIn}
                      onPressOut={cancelButtonPressHandlers.onPressOut}
                      testID={
                        isNativeAppPromo
                          ? WEB_NATIVE_APP_PROMO_TEST_IDS.CONTINUE_WEB
                          : 'dialog-cancel-button'
                      }
                    >
                      <Text style={[styles.buttonText, styles.secondaryButtonText]}>
                        {localDialog.cancelText || 'Cancel'}
                      </Text>
                    </SoundPressable>
                  </Animated.View>
                ) : null}

                {hasDestructive ? (
                  <Animated.View style={destructiveButtonAnimatedStyle}>
                    <SoundPressable
                      playTapSound={false}
                      style={({ pressed, hovered }) => [
                        styles.button,
                        styles.destructiveSecondaryButton,
                        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                        pressed && styles.buttonPressed,
                        hovered && Platform.OS === 'web' && styles.buttonHover,
                      ]}
                      onPress={handleDestructive}
                      onPressIn={destructiveButtonPressHandlers.onPressIn}
                      onPressOut={destructiveButtonPressHandlers.onPressOut}
                      testID="dialog-destructive-button"
                    >
                      <Text style={[styles.buttonText, styles.destructiveSecondaryButtonText]}>
                        {localDialog.destructiveText}
                      </Text>
                    </SoundPressable>
                  </Animated.View>
                ) : null}

                <Animated.View style={confirmButtonAnimatedStyle}>
                  <SoundPressable
                    playTapSound={false}
                    disabled={isDeleteAccount && !canConfirmDeleteAccount}
                    style={({ pressed, hovered }) => [
                      styles.button,
                      styles.primaryButton,
                      primaryButtonByType,
                      isDeleteAccount && !canConfirmDeleteAccount && styles.primaryButtonDisabled,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.buttonPressed,
                      hovered && Platform.OS === 'web' && styles.buttonHover,
                    ]}
                    onPress={handleConfirm}
                    onPressIn={confirmButtonPressHandlers.onPressIn}
                    onPressOut={confirmButtonPressHandlers.onPressOut}
                    testID={
                      isNativeAppPromo
                        ? WEB_NATIVE_APP_PROMO_TEST_IDS.GET_APP
                        : 'dialog-confirm-button'
                    }
                  >
                    <Text style={[styles.buttonText, styles.primaryButtonText]}>
                      {localDialog.confirmText || (isAlert ? 'OK' : 'Confirm')}
                    </Text>
                  </SoundPressable>
                </Animated.View>
              </View>
            </View>
          </Animated.View>
        </Animated.View>
      </Pressable>
    </Modal>
  );
};

